import { Frown, RefreshCcw } from "lucide-react";
import { ComponentType } from "react";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import { toast } from "sonner";
import { Button } from "../ui/button";

type Config = {
  component: string;
};

const FallbackComponent: React.FC<FallbackProps & { config: Config }> = ({
  error,
}) => {
  toast.error("Something went wrong! Please refresh the page and try again.");
  return (
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center p-6 bg-white border border-slate-200 rounded-xl shadow-md w-[90%] max-w-sm">
      <div className="flex items-center justify-center gap-3 text-orange-500 mb-3">
        <Frown className="w-7 h-7" />
        <p className="text-lg font-medium">Hmm... something went wrong.</p>
      </div>

      <p className="text-slate-500 mb-4">
        Try refreshing the page to continue.
      </p>

      <Button
        variant="outline"
        className="border-orange-500 text-orange-500 hover:bg-orange-50"
        onClick={() => window.location.reload()}
      >
        <RefreshCcw className="w-4 h-4 mr-2" />
        Refresh Page
      </Button>

      {error && (
        <p className="bg-orange-50 border border-orange-200 text-orange-400 rounded p-3 text-sm mt-4">
          {error.toString()}
        </p>
      )}
    </div>
  );
};

const withErrorBoundary =
  (config: Config) =>
  <P extends object>(Component: ComponentType<P>) => {
    const EnhancedComponent = (props: P) => {
      return (
        <ErrorBoundary
          FallbackComponent={(fallbackProps) => (
            <FallbackComponent {...fallbackProps} config={config} />
          )}
        >
          <Component {...props} />
        </ErrorBoundary>
      );
    };

    return EnhancedComponent;
  };

export default withErrorBoundary;
